import { Spinner } from '@iwoca/orion';
import { Outlet } from 'react-router';

import { NewRoutesWrapperArrayType, NewRoutesWrapperType } from './utils';
import { isDemoHostAllowed } from '../demo/utils/isDemoHostAllowed';

const DemoEntrypoint = ({ isStart }: { isStart: boolean }) => {
  if (isStart) {
    localStorage.setItem('demoModeEnabled', 'true');
    // we need to navigate outside of react-router to reset state
    // eslint-disable-next-line react-hooks/immutability -- intentional full-page navigation
    window.location.href = '/pay/dashboard/?state_key=demo_system';
  } else {
    localStorage.removeItem('demoModeEnabled');
    localStorage.removeItem('demoJourneyType');
    localStorage.removeItem('demoInterestRates');
    localStorage.removeItem('demoFlow');
    localStorage.removeItem('demoSpendingLimitGranted');
    // this will get redirected off to eden etc
    // eslint-disable-next-line react-hooks/immutability -- intentional full-page navigation
    window.location.href = '/pay/dashboard/';
  }

  return (
    <div className="flex h-full w-full items-center justify-center">
      <Spinner />
    </div>
  );
};

// The demo storefront / invoice / pay-now pages are lazy-loaded so their code is
// split into separate chunks and never shipped in the bundle production
// customers download. The routes themselves are only registered on demo hosts
// (see DEMO_ROUTES below), so this code is unreachable in production.
const ENTRYPOINTS = [
  {
    path: 'demo/start',
    element: <DemoEntrypoint isStart={true} />,
  },
  {
    path: 'demo/end',
    element: <DemoEntrypoint isStart={false} />,
  },
  {
    path: 'demo/shop',
    lazy: async () => {
      const { DemoStorefront } =
        await import('../demo/components/DemoStorefront');
      return { element: <DemoStorefront /> };
    },
  },
  {
    path: 'demo/basket',
    lazy: async () => {
      const { DemoStorefrontBasket } =
        await import('../demo/components/DemoStorefrontBasket');
      return { element: <DemoStorefrontBasket /> };
    },
  },
  {
    path: 'demo/checkout',
    lazy: async () => {
      const { DemoStorefrontCheckout } =
        await import('../demo/components/DemoStorefrontCheckout');
      return { element: <DemoStorefrontCheckout /> };
    },
  },
  {
    path: 'demo/confirmation',
    lazy: async () => {
      const { DemoStorefrontConfirmation } =
        await import('../demo/components/DemoStorefrontConfirmation');
      return { element: <DemoStorefrontConfirmation /> };
    },
  },
  {
    path: 'demo/invoice',
    lazy: async () => {
      const { DemoInvoiceEmail } =
        await import('../demo/components/DemoInvoiceEmail');
      return { element: <DemoInvoiceEmail /> };
    },
  },
  {
    path: 'demo/pay-now-bank',
    lazy: async () => {
      const { DemoPayNowBank } =
        await import('../demo/components/DemoPayNowBank');
      return { element: <DemoPayNowBank /> };
    },
  },
  {
    // Render the real PayNowConfirmation page (not a mock) — it reads payment_id
    // from the URL and the mocked payment_data drives its "complete" receipt.
    path: 'demo/pay-now-confirmation',
    lazy: async () => {
      const { PayNowConfirmation } =
        await import('../Buyer/PayNowConfirmation/PayNowConfirmation');
      return { element: <PayNowConfirmation /> };
    },
  },
] as const satisfies NewRoutesWrapperType['paths'];

const DEMO_ENTRYPOINTS = {
  wrapper: <Outlet />,
  paths: ENTRYPOINTS,
} as const satisfies NewRoutesWrapperType;

// Only register demo routes on local/staging/review hosts. In production these
// routes do not exist, so a customer navigating to /pay/demo/* falls through to
// PageNotFound rather than rendering a mock storefront.
export const DEMO_ROUTES: NewRoutesWrapperArrayType = isDemoHostAllowed()
  ? [DEMO_ENTRYPOINTS]
  : [];
